import {
  ActivityFilterKey,
  ActivityFilterModel,
  ActivityOptions,
  ActivityOrderBy,
  DateTypeFilter,
  getActivityFilter,
  Severity,
  useActivityListFilter,
  useCurrentAccount,
  useCurrentWorkspace,
  useCustomQuery,
  useSetActivityListFilter,
} from '@gripp/shared-logic';
import { yupResolver } from '@hookform/resolvers/yup';
import { keepPreviousData } from '@tanstack/react-query';
import { forwardRef, useCallback, useEffect, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { array, boolean, string, object as yupObject } from 'yup';
import { useSlideLayoutSafe } from '../slide-layout';
import {
  FilterScreenLayout,
  FilterScreenLayoutProps,
} from './filterScreenLayout';
import { ACTIVITY_SECTIONS } from './filterSectionTypes';

export type ActivityFilterScreenHandle = {
  onReset: () => Promise<void>;
};

const formSchema = yupObject({
  orderBy: string().optional().nullable(),
  severity: array().of(string()).optional().nullable(),
  groupIds: array().of(string()).optional().nullable(),
  labelIds: array().of(string()).optional().nullable(),
  isCombinedGroupsFilter: boolean().optional().nullable(),
  activityOption: string().optional().nullable(),
  dateType: string().optional().nullable(),
  startDate: string().optional().nullable(),
  endDate: string().optional().nullable(),
});

export type ActivityFilterScreenProps = {
  mode: 'page' | 'modal';
  onBack?: () => void;
  onDismiss?: () => void;
  isVisible?: boolean;
  filterKey?: ActivityFilterKey;
  bottomBar?: FilterScreenLayoutProps['bottomBar'];
  assetId?: string;
};

export const ActivityFilterScreen = forwardRef<
  ActivityFilterScreenHandle,
  ActivityFilterScreenProps
>(function ActivityFilterScreen(
  {
    mode,
    onBack,
    onDismiss,
    isVisible,
    filterKey = ActivityFilterKey.Activity,
    bottomBar,
    assetId,
  }: ActivityFilterScreenProps,
  ref
) {
  const { t } = useTranslation();
  const slideLayout = useSlideLayoutSafe();
  const { data: filter } = useActivityListFilter(filterKey);
  const { mutateAsync: setActivityListFilter } =
    useSetActivityListFilter(filterKey);
  const currentWorkspace = useCurrentWorkspace(true);
  const { account } = useCurrentAccount();

  const getInitialSeverity = () => {
    if (!filter?.severity || filter.severity.length === 0) return [];
    const allSeverities = Object.values(Severity);
    const hasAllSeverities =
      filter.severity.length === allSeverities.length &&
      allSeverities.every((s) => filter.severity?.includes(s));
    return hasAllSeverities ? [] : filter.severity;
  };

  const formMethods = useForm({
    resolver: yupResolver(formSchema),
    mode: 'onChange',
    values: {
      orderBy: filter?.orderBy ?? ActivityOrderBy.MostRecentUpdated,
      severity: getInitialSeverity(),
      groupIds: filter?.groupIds ?? [],
      labelIds: filter?.labelIds ?? [],
      isCombinedGroupsFilter: filter?.isCombinedGroupsFilter ?? false,
      activityOption: filter?.activityOption ?? ActivityOptions.EVERYTHING,
      dateType: filter?.dateType ?? DateTypeFilter.ALL,
      startDate: filter?.startDate ?? '',
      endDate: filter?.endDate ?? '',
    },
  });

  const { control, reset, watch, setValue } = formMethods;
  const formData = watch();
  const selectedDateType = watch('dateType');

  const isGlobalAdmin = account?.isGlobalAdmin?.() || false;
  const workspaceId =
    currentWorkspace?.id ??
    (isGlobalAdmin ? undefined : account?.workspace?.id);

  const buildServerFilter = () => {
    const payload: any = { ...formData };
    if (!payload.severity || payload.severity.length === 0)
      delete payload.severity;
    if (payload.activityOption === ActivityOptions.EVERYTHING)
      delete payload.activityOption;
    if (!payload.groupIds || payload.groupIds.length === 0)
      delete payload.groupIds;
    if (!payload.labelIds || payload.labelIds.length === 0)
      delete payload.labelIds;
    const hasActiveFilters =
      payload.severity ||
      payload.activityOption ||
      payload.groupIds?.length > 0 ||
      payload.labelIds?.length > 0 ||
      payload.dateType !== DateTypeFilter.ALL;
    if (!hasActiveFilters && !workspaceId && !isGlobalAdmin) return undefined;
    if (!hasActiveFilters && !workspaceId && isGlobalAdmin) return {};
    const activityFilterModel = new ActivityFilterModel({
      ...filter,
      ...payload,
    });
    return workspaceId
      ? getActivityFilter(activityFilterModel, workspaceId)
      : activityFilterModel.getFilter();
  };

  const serverFilterForCount = buildServerFilter();

  if (serverFilterForCount && assetId) {
    serverFilterForCount.asset = { eq: assetId };
  }

  const { data: countData } = useCustomQuery({
    cacheKey: [
      'activities',
      'filterCount',
      isGlobalAdmin ? 'global' : workspaceId || 'none',
      JSON.stringify(serverFilterForCount),
    ],
    query: `
      query FindIssuesCount($filter: FindIssuesFilter, $page: PageRequest) {
        issue {
          findIssues(filter: $filter, page: $page) {
            count
          }
        }
      }
    `,
    variables: { filter: serverFilterForCount, page: { limit: 1, offset: 0 } },
    options: {
      enabled: !!serverFilterForCount,
      placeholderData: keepPreviousData,
    },
  });
  const resultsCount = countData?.issue?.findIssues?.count ?? 0;

  useEffect(() => {
    slideLayout?.setColumnTitle(`${t('common.filter')} (${resultsCount})`);
    return () => slideLayout?.setColumnTitle(undefined);
  }, [resultsCount, slideLayout]);

  const onCloseAfterApply = mode === 'page' ? onBack : onDismiss;

  const onSubmit = async (data: any) => {
    const payload = { ...data };
    if (!payload.severity || payload.severity.length === 0)
      payload.severity = undefined;
    await setActivityListFilter(
      new ActivityFilterModel({ ...filter, ...payload })
    );
    onCloseAfterApply?.();
  };

  const onReset = useCallback(async () => {
    const defaultData = {
      orderBy: ActivityOrderBy.MostRecentUpdated,
      severity: [],
      activityOption: ActivityOptions.EVERYTHING,
      groupIds: [],
      labelIds: [],
      isCombinedGroupsFilter: false,
      dateType: DateTypeFilter.ALL,
      startDate: '',
      endDate: '',
    };
    reset(defaultData);
    await setActivityListFilter(
      new ActivityFilterModel({
        ...filter,
        ...defaultData,
        severity: undefined,
      })
    );
  }, [reset, setActivityListFilter, filter]);

  useImperativeHandle(ref, () => ({ onReset }), [onReset]);

  const canReset = () =>
    (formData.severity && formData.severity.length > 0) ||
    formData.activityOption !== ActivityOptions.EVERYTHING ||
    (formData.groupIds && formData.groupIds.length > 0) ||
    (formData.labelIds && formData.labelIds.length > 0) ||
    formData.dateType !== DateTypeFilter.ALL ||
    formData.orderBy !== ActivityOrderBy.MostRecentUpdated;

  return (
    <FilterScreenLayout
      mode={mode}
      onBack={onBack}
      onDismiss={onDismiss}
      isVisible={isVisible}
      formMethods={formMethods}
      resultsCount={resultsCount}
      viewResultsTranslationKey="activity.filter.viewResults"
      onReset={onReset}
      canReset={canReset}
      onSubmit={onSubmit}
      variant="activity"
      sections={ACTIVITY_SECTIONS}
      control={control}
      setValue={setValue}
      selectedDateType={selectedDateType ?? ''}
      workspaceId={workspaceId}
      isGlobalAdmin={isGlobalAdmin}
      bottomBar={bottomBar}
    />
  );
});
